import { logger } from './logger'

/**
 * Утилита для динамической загрузки Telegram WebApp API
 * Загружает скрипт только когда это действительно нужно
 */

let isLoading = false
let isLoaded = false

export async function loadTelegramWebApp(): Promise<boolean> {
  // Если уже загружен
  if (isLoaded && window.Telegram?.WebApp) {
    return true
  }
  
  // Если уже загружается
  if (isLoading) {
    return new Promise((resolve) => {
      const checkLoaded = () => {
        if (isLoaded && window.Telegram?.WebApp) {
          resolve(true)
        } else if (!isLoading) {
          resolve(false)
        } else {
          setTimeout(checkLoaded, 100)
        }
      }
      checkLoaded()
    })
  }
  
  isLoading = true
  
  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = 'https://telegram.org/js/telegram-web-app.js'
    script.async = true
    
    script.onload = () => {
      isLoaded = true
      isLoading = false
      
      if (process.env.NODE_ENV === 'development') {
        logger.log('📱 Telegram WebApp API загружен')
      }
      resolve(true)
    }
    
    script.onerror = () => {
      isLoading = false
      if (process.env.NODE_ENV === 'development') {
        logger.error('❌ Ошибка загрузки Telegram WebApp API')
      }
      resolve(false)
    }
    
    document.head.appendChild(script)
  })
}

/**
 * Проверяет, запущено ли приложение в Telegram
 * Основано на проверке User Agent и других признаков
 */
export function isTelegramEnvironment(): boolean {
  // 1. Проверяем User Agent
  const userAgent = navigator.userAgent.toLowerCase()
  if (userAgent.includes('telegram')) {
    return true
  }
  
  // 2. Проверяем наличие Telegram-специфичных параметров в URL
  const urlParams = new URLSearchParams(window.location.search)
  const hash = window.location.hash
  
  if (urlParams.get('tgWebAppPlatform') || 
      urlParams.get('tgWebAppVersion') ||
      hash.includes('tgWebAppData=')) {
    return true
  }
  
  // 3. Проверяем путь URL (возвращаем как было)
  if (window.location.pathname.startsWith('/miniapp')) {
    return true
  }
  
  return false
}

/**
 * Инициализирует Telegram WebApp API если нужно
 */
export async function initTelegramWebAppIfNeeded(): Promise<boolean> {
  if (!isTelegramEnvironment()) {
    if (process.env.NODE_ENV === 'development') {
      logger.log('🌐 Не Telegram окружение - пропускаем загрузку WebApp API')
    }
    return false
  }
  
  try {
    const loaded = await loadTelegramWebApp()
    if (loaded && window.Telegram?.WebApp) {
      // Инициализируем WebApp с обработкой ошибок
      try {
        window.Telegram.WebApp.ready()
        window.Telegram.WebApp.expand()
        
        if (process.env.NODE_ENV === 'development') {
          logger.log('🎨 Telegram WebApp инициализирован', {
            version: window.Telegram.WebApp.version,
            platform: window.Telegram.WebApp.platform,
            colorScheme: window.Telegram.WebApp.colorScheme,
            isExpanded: window.Telegram.WebApp.isExpanded
          })
        }
        
        return true
      } catch (initError) {
        if (process.env.NODE_ENV === 'development') {
          logger.warn('⚠️ Ошибка при инициализации Telegram WebApp (возможно, тестирование в браузере):', initError)
        }
        // Возвращаем true, чтобы система продолжила работу в "эмуляции" Telegram режима
        return true
      }
    }
    
    return false
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      logger.error('❌ Ошибка при загрузке Telegram WebApp API:', error)
    }
    // В случае ошибки возвращаем false, чтобы система переключилась в браузерный режим
    return false
  }
}
